<template>
  <div class="w-full mx-auto sm:max-w-5xl 2xl:max-w-6xl 3xl:max-w-8xl 4xl:max-w-9xl">
    <h1 class="zt-settings-title mb-4" data-testid="page-title">Workflows & Integrations</h1>
    <p class="zt-text-md mb-4">
      Zight provides seamless integration options to supercharge your workflow automation. From embedding Zight links anywhere to leveraging webhooks that
      notify your tools about new items or activity, we've got you covered. Plus, Zight offers full integrations with popular platforms like Zendesk, Jira,
      Microsoft Teams and Slack, making collaboration effortless.
    </p>

    <!-- Show login prompt for logged out users -->
    <template v-if="!isLoggedIn">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6" data-testid="login-prompt">
        <h2 class="text-lg font-semibold text-blue-900 mb-2">Sign in to access integrations</h2>
        <p class="text-blue-700 mb-4">To view and manage your integrations, please sign in to your Zight account.</p>
        <div class="flex gap-3">
          <a :href="loginUrl" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors" data-testid="sign-in-button"> Sign In </a>
          <a
            :href="signupUrl"
            class="bg-white text-blue-600 border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
            data-testid="create-account-button"
          >
            Create Account
          </a>
        </div>
      </div>
    </template>

    <!-- Show 1-click integrations for logged in users -->
    <template v-if="isLoggedIn">
      <div class="workflows-section mt-8" data-testid="workflows-section">
        <h2 class="text-xl font-semibold mb-3">Workflows</h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Set up automatic or 1-click integrations with Zight. Automatically create content from step-by-step guides or update your customer tickets when
          receiving videos.
        </p>
        <p v-if="!hasBusinessPlan" class="zt-info-box mb-6" data-testid="plan-notice">
          Please note: 1-click integrations are available on our Enterprise plan. Contact your customer success manager to help you get set up.
        </p>
        <template v-if="hasIntegrations">
          <div data-testid="integrations-grid-container">
            <IntegrationsGrid />
          </div>
        </template>
        <template v-else-if="integrationStore.loading">
          <div class="flex items-center justify-center py-8" data-testid="loading-state">
            <div class="text-gray-500">Loading integrations...</div>
          </div>
        </template>
        <template v-else-if="integrationStore.error">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="error-state">
            <p class="text-red-700">{{ integrationStore.error }}</p>
          </div>
        </template>
        <template v-else>
          <p data-testid="no-integrations-message">No integrations available. Contact your account admin to enable it.</p>
        </template>
      </div>
    </template>

    <!-- Static Integrations Section - Always show for all users -->
    <div class="static-integrations-section mt-8 pt-8 border-t border-slate-300 dark:border-zinc-700" data-testid="apps-section">
      <h2 class="text-xl font-semibold mb-3">Apps</h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Install our Zight apps into the different tools you already use in your day-to-day operations.
      </p>

      <div class="static-integrations-grid">
        <div
          v-for="integration in sortedStaticIntegrations"
          :key="integration.name"
          class="static-integration-card"
          :class="{ restricted: integration.restricted }"
        >
          <div class="flex-grow">
            <h3 class="static-integration-name">{{ integration.name }}</h3>
            <p class="static-integration-description">{{ integration.description }}</p>
            <div class="static-integration-button">
              <template v-if="integration.restricted">
                <button
                  class="static-setup-button restricted-button"
                  @click="trackIntegrationClick(integration.name)"
                  disabled
                  data-testid="restricted-setup-button"
                >
                  Setup
                </button>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1" data-testid="restriction-notice">Available on Enterprise plan.</p>
              </template>
              <template v-else>
                <a
                  :href="integration.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="static-setup-button"
                  @click="trackIntegrationClick(integration.name)"
                  data-testid="setup-button"
                  >Setup</a
                >
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Coming Soon Integrations Section -->
    <div class="coming-soon-integrations-section mt-8 pt-8 border-t border-slate-300 dark:border-zinc-700" data-testid="coming-soon-section">
      <h2 class="text-xl font-semibold mb-3">Coming Soon</h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">Request these integrations to help us prioritize development.</p>

      <div class="static-integrations-grid">
        <ComingSoonIntegrationCard
          v-for="integration in sortedComingSoonIntegrations"
          :key="integration.name"
          :integration="integration"
          @request-integration="handleRequestIntegration"
        />
      </div>
    </div>

    <!-- Webhooks Settings - Always show for all users -->
    <WebhooksSettings />
  </div>
</template>

<script lang="ts" setup>
import IntegrationsGrid from '@/modules/integrations/components/IntegrationsGrid.vue';
import WebhooksSettings from '@/modules/integrations/components/WebhooksSettings.vue';
import ComingSoonIntegrationCard from '@/modules/integrations/components/ComingSoonIntegrationCard.vue';
import { onMounted, computed } from 'vue';
import { useIntegrationStore } from '@/modules/integrations/store/useIntegrationStore';
import { useUserStore } from '@/store/useUserStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useToast } from '@/composables/useToast';

const integrationStore = useIntegrationStore();
const userStore = useUserStore();
const settingsStore = useSettingsStore();
const organizationStore = useOrganizationStore();
const { track } = useTrack();
const { success } = useToast();

const hasIntegrations = computed(() => integrationStore.integrations.length > 0);
const isLoggedIn = computed(() => userStore.isLoggedIn);
const loginUrl = computed(() => settingsStore.loginUrl || '/login');
const signupUrl = computed(() => settingsStore.signupUrl || '/signup');

// Plan-based restrictions for 1-click integrations
const getBusinessPlans = () => ['business-annual', 'business-monthly'];
const getRestrictedIntegrations = () => ['Zendesk', 'Jira', 'Confluence', 'Notion'];

const hasBusinessPlan = computed(() => {
  const planCode = organizationStore.planCode?.toLowerCase();
  return planCode && getBusinessPlans().includes(planCode);
});

const isIntegrationRestricted = (integrationName: string) => {
  return getRestrictedIntegrations().includes(integrationName) && !hasBusinessPlan.value;
};

// Static integrations data
const getStaticIntegrationsData = () => [
  {
    name: 'Zendesk',
    description: 'Create and share videos directly from Zendesk to provide better customer support',
    url: 'https://www.zendesk.com/marketplace/apps/support/110120/zight/',
    hasRestriction: true,
  },
  {
    name: 'Jira',
    description: 'Create videos directly from your Jira tickets',
    url: 'https://marketplace.atlassian.com/apps/1228444/cloudapp-screen-recorder-for-jira?tab=overview&hosting=cloud',
    hasRestriction: true,
  },
  {
    name: 'Slack',
    description: 'Create videos directly from Slack, no app install needed on your computer, and receive notifications from engagement on your content.',
    url: 'https://cloudapp.slack.com/marketplace/A037KHLBMKK-zight-screen-recorder',
    hasRestriction: false,
  },
  {
    name: 'Teams',
    description: 'Get notified in Microsoft Teams when someone views your Zight videos, gifs and screenshots.',
    url: 'https://appsource.microsoft.com/en-us/product/office/wa200005310?tab=overview',
    hasRestriction: false,
  },
  {
    name: 'Lucid',
    description:
      "Share information faster and with more context and clarity on Lucid's collaborative platform with the power of Zight's screen and video recordings natively where work is happening.",
    url: 'https://zight.com/integrations/lucid/',
    hasRestriction: false,
  },
  {
    name: 'Zight SDK',
    description: 'Add video recording capabilities to your products',
    url: 'https://zight.com/sdk/',
    hasRestriction: false,
  },
];

// Sort integrations alphabetically and add computed restricted property
const sortedStaticIntegrations = computed(() => {
  return [...getStaticIntegrationsData()]
    .map(integration => ({
      ...integration,
      restricted: integration.hasRestriction ? isIntegrationRestricted(integration.name) : false,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const trackIntegrationClick = (integrationName: string) => {
  // Track the integration click event using the proper analytics service
  track(TrackEvents.INTEGRATIONS_SETUP, {
    app: integrationName,
  });
};

// Coming soon integrations data
const getComingSoonIntegrationsData = () => [
  {
    name: 'Salesforce Service Cloud',
    description: "Enhance your Salesforce workflows with Zight's screen recording capabilities",
  },
  {
    name: 'ServiceNow',
    description: 'Streamline IT service management with integrated screen recording and sharing',
  },
  {
    name: 'Intercom',
    description: 'Enhance customer conversations with visual context through screen recordings and screenshots',
  },
  {
    name: 'Freshdesk',
    description: 'Provide faster support resolution with visual troubleshooting guides and screen recordings',
  },
];

const sortedComingSoonIntegrations = computed(() => {
  return [...getComingSoonIntegrationsData()].sort((a, b) => a.name.localeCompare(b.name));
});

const handleRequestIntegration = (integrationName: string) => {
  // Track the integration request
  trackIntegrationClick(integrationName);

  // Show toast confirmation
  success(`Thanks for your interest in ${integrationName}! We've recorded your request and will prioritize development based on demand.`, {
    title: 'Integration Request Submitted',
  });
};

onMounted(() => {
  // Only fetch integrations if user is logged in
  if (isLoggedIn.value) {
    integrationStore.fetchIntegrations();
    // Note: Organization store should be initialized by parent component or route guard
    // The organization data is typically loaded at the app level
  }
});
</script>

<style scoped>
.zt-settings-title {
  font-size: 2rem; /* Example size, adjust as needed */
  font-weight: bold;
  margin-bottom: 1rem; /* Added margin-bottom for spacing */
}

.zt-text-md {
  font-size: 1rem; /* Example size, adjust as needed */
  line-height: 1.5;
  margin-bottom: 1rem; /* Added margin-bottom for spacing */
}

.zt-info-box {
  background-color: #f0f7ff; /* Example blueish background */
  border: 1px solid #cfe2f3; /* Example border */
  padding: 1rem;
  border-radius: 0.25rem;
  margin-top: 1rem;
  margin-bottom: 2rem; /* Added margin-bottom for spacing before the grid */
}

/* Original zt-dashboard-title, kept in case it's used elsewhere or if h1 needs similar base styling */
.zt-dashboard-title {
  font-size: 1.25rem;
  font-weight: 500;
  display: flex;
  flex-shrink: 1;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

/* Static Integrations Styles */
.static-integrations-grid {
  display: grid;
  gap: 1.5rem;
  margin-top: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.static-integration-card {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  transition: all 0.2s ease;
}

.static-integration-card:hover {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.dark .static-integration-card {
  background-color: #27272a;
  border-color: #3f3f46;
}

.static-integration-icon {
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-radius: 50%;
  background-color: white;
  border: 1px solid #cbd5e1;
  flex-shrink: 0;
}

.dark .static-integration-icon {
  background-color: #3f3f46;
  border-color: #3f3f46;
}

.static-integration-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.dark .static-integration-name {
  color: #d4d4d8;
}

.static-integration-description {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.dark .static-integration-description {
  color: #a1a1aa;
}

.static-integration-button {
  margin-top: 1rem;
}

.static-setup-button {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #1e293b;
  color: white;
  text-decoration: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.static-setup-button:hover {
  background-color: #334155;
}

.dark .static-setup-button {
  background-color: #6b7280;
}

.dark .static-setup-button:hover {
  background-color: #9ca3af;
}

.coming-soon-badge {
  display: inline-block;
  background-color: #f59e0b;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  margin-right: 0.5rem;
  text-transform: lowercase;
}

.request-integration-button {
  background-color: #3b82f6 !important;
  color: white !important;
  cursor: pointer !important;
  opacity: 1;
}

.request-integration-button:hover {
  background-color: #2563eb !important;
}

.static-integration-card.coming-soon {
  opacity: 1;
  border-color: #f59e0b;
}

.static-integration-card.coming-soon .static-integration-name {
  color: #0f172a;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.dark .static-integration-card.coming-soon .static-integration-name {
  color: #d4d4d8;
}

.static-integration-card.coming-soon .static-integration-description {
  color: #475569;
}

.dark .static-integration-card.coming-soon .static-integration-description {
  color: #a1a1aa;
}

.restricted-button {
  background-color: #d1d5db !important;
  color: #6b7280 !important;
  cursor: not-allowed !important;
  opacity: 0.5;
}

.restricted-button:hover {
  background-color: #d1d5db !important;
}

.static-integration-card.restricted {
  opacity: 0.5;
}

.static-integration-card.restricted .static-integration-name {
  color: #6b7280;
}

.dark .static-integration-card.restricted .static-integration-name {
  color: #9ca3af;
}

.static-integration-card.restricted .static-integration-description {
  color: #9ca3af;
}

.dark .static-integration-card.restricted .static-integration-description {
  color: #71717a;
}
</style>
